/**
 * English: the source language.
 *
 * Every user-visible string on the guest site is here, and this file is where a
 * copy edit happens. `typeof en` is the type every other catalogue is checked
 * against, so a key added here without a translation is a compile error in
 * `nl.ts` on the next build — not a Dutch guest finding an English sentence in
 * the middle of a booking form.
 *
 * Keys are structural and grouped by the thing that shows them, never keyed by
 * the English text itself: keying on the text turns every copy edit into a
 * rename across every catalogue, which is how catalogues drift.
 *
 * `{name}` interpolates. A `{ one, other }` value is a plural chosen on the
 * `count` passed with it. Emphasis inside a sentence is marked `*so*` and
 * rendered by `<Rich>`, so a translator can move it to the word that carries it
 * in their language.
 *
 * What is NOT here: accommodation names, attribute labels and the park's own
 * name. Those are data the operator typed, and §8 is explicit that operator
 * data is never translated. Country names are not here either — they come from
 * `Intl.DisplayNames`, so a guest reads them in their own language without one
 * of the 249 names existing in this repository.
 */

export interface Plural {
  one: string;
  other: string;
}

export const en = {
  /* ------------------------------------------------------------------ shell */
  'app.title': 'Book a stay',
  'app.language': 'Language',
  'app.appearance': 'Appearance',
  'app.schemeSystem': 'Match my device',
  'app.schemeLight': 'Light',
  'app.schemeDark': 'Dark',
  'app.back': 'Back to the search',

  /* ------------------------------------------------------------- the search */
  'search.heading': 'Find a stay',
  'search.stepLength': 'How long?',
  'search.stepDates': 'When?',
  'search.stepParty': "Who's coming?",
  'search.step': 'Step {n}',
  'search.nights': {
    one: '{count} night',
    other: '{count} nights',
  } as Plural,
  'search.customLength': 'Another length',
  'search.nightsLabel': 'Number of nights',
  'search.datesLocked': 'Choose a length above.',
  'search.prevMonth': 'Previous month',
  'search.nextMonth': 'Next month',
  'search.loadingDates': 'Checking which dates are free…',
  'search.arrival': 'Arrival',
  'search.departure': 'Departure',
  'search.dayFree': '{date} — {nights} available',
  'search.dayFull': '{date} — not available for {nights}',
  'search.dayPast': '{date} — in the past',
  'search.noDates': 'No arrival date in {month} can take {nights}. Try a shorter stay, or look at the next month.',
  'search.filteredFor': 'Dates shown for the {count} matching your filters.',
  'search.matches': {
    one: '{count} accommodation',
    other: '{count} accommodations',
  } as Plural,
  'search.clearDate': 'Choose a different date',
  'search.pickDate': 'Choose',
  'search.gapRange': '{from} \u2013 {to}',
  'search.gapClosed': 'the park is closed',
  'search.gapNoDeparture': 'no departure possible from this arrival',
  'search.pickArrival': 'Choose your arrival',
  'search.pickDeparture': 'Choose your departure',
  'search.clearDates': 'Clear',
  'search.closeDates': 'Close the calendar',
  'search.datesDone': 'Done',
  'search.dayArrival': 'Arrive {date}',
  'search.dayDeparture': 'Leave {date}',
  'search.dayNoArrival': '{date} — no arrival possible',
  'search.dayNoDeparture': '{date} — cannot leave on this day',
  'search.filterEmpty': 'Nothing matches your filters. Clear one to see dates again.',

  /* ------------------------------------------------------------------ party */
  'party.adult': 'Adults',
  'party.child': 'Children',
  'party.infant': 'Babies',
  'party.pet': 'Dogs',
  'party.ageFrom': '{age} and over',
  'party.ageBetween': '{from} to {until} years',
  'party.ageUnder': 'under {age}',
  'party.fewer': 'One fewer {what}',
  'party.more': 'One more {what}',
  'party.summary': {
    one: '{count} guest',
    other: '{count} guests',
  } as Plural,

  /* ---------------------------------------------------------------- filters */
  'filters.heading': 'Narrow it down',
  'filters.show': 'Filters',
  'filters.hide': 'Done',
  'filters.clear': 'Clear all',
  'filters.any': 'Any',
  'filters.min': 'At least',
  'filters.max': 'At most',
  'filters.only': 'Only with this',
  'filters.contains': 'Contains',
  'filters.active': {
    one: '{count} filter',
    other: '{count} filters',
  } as Plural,

  /* ---------------------------------------------------------------- results */
  'results.heading': 'What we can offer',
  'results.quoting': 'Working out prices…',
  'results.none': 'Nothing matches those filters. Clear one to see more.',
  'results.noneForDates': 'Nothing is free for those dates. A shorter stay or a different week usually is.',
  'results.chooseDate': 'Pick an arrival date to see prices.',
  'results.unavailable': 'Not free for these dates',
  'results.free': {
    one: '{count} left',
    other: '{count} left',
  } as Plural,
  'results.total': 'Total for {stay}',
  'results.perNight': '{price} a night',
  'results.extras': 'Includes {price} of charges and taxes',
  'results.noPrice': 'Ask us for a price',
  'results.bookingDirect': 'Bookable',
  'results.bookingContact': 'On request',
  'results.bookingIndicative': 'Guide price',
  'results.bookingIndicativeWhy': 'Reception confirms the final amount.',
  'results.restrictionWhy': 'Why: {reason}',
  'results.priceChanged': 'The price has changed — this is the new one.',
  'results.choose': 'Request this stay',
  'results.chosen': 'Chosen',
  'results.attributesHeading': 'Details',
  'results.attrYes': 'Yes',
  'results.attrNo': 'No',

  /* -------------------------------------------------------- single unit box */
  'unit.heading': 'Prices and availability',
  'unit.pickLength': 'How long would you like to stay?',
  'unit.pickDate': 'Choose an arrival date',
  'unit.availableOn': 'Free on {date}',
  'unit.request': 'Request these dates',
  'unit.noPrice': 'Ask the park for a price',

  /* --------------------------------------------------------------- enquiry */
  'enquiry.heading': 'Request this stay',
  'enquiry.intro': 'A request is not a booking yet. Reception reads it and replies personally.',
  'enquiry.stay': '{accommodation}, {arrival} to {departure}',
  'enquiry.aboutYou': 'About you',
  'enquiry.name': 'Name',
  'enquiry.email': 'Email',
  'enquiry.country': 'Country',
  'enquiry.countryWhy': 'The park needs this for its tourist tax return.',
  'enquiry.countryPlaceholder': 'Choose a country',
  'enquiry.countryNearby': 'Nearby',
  'enquiry.countryAll': 'All countries',
  'enquiry.phone': 'Phone',
  'enquiry.address': 'Address',
  'enquiry.street': 'Street',
  'enquiry.houseNumber': 'Number',
  'enquiry.houseNumberAddition': 'Addition',
  'enquiry.postalCode': 'Postcode',
  'enquiry.city': 'Town',
  'enquiry.unit': 'Which one would you like?',
  'enquiry.unitAny': 'No preference',
  'enquiry.unitTaken': '{unit} — not free on these dates',
  'enquiry.errUnit': 'That one has just been taken. Pick another, or leave it to us.',
  'enquiry.vehicle': 'Vehicle',
  'enquiry.plateToggle': "I'm bringing a vehicle",
  'enquiry.plate': 'Registration',
  'enquiry.plateHint': 'It lets the barrier recognise you on arrival.',
  'enquiry.plateLookup': 'Look it up',
  'enquiry.plateLooking': 'Looking…',
  'enquiry.plateFound': 'Is this your vehicle?',
  'enquiry.plateWrong': "That's not mine",
  'enquiry.comment': 'Anything we should know?',
  'enquiry.commentHint': 'A late arrival, a quiet spot.',
  'enquiry.optional': 'optional',
  'enquiry.required': 'required',
  'enquiry.submit': 'Send the request',
  'enquiry.sending': 'Sending…',
  'enquiry.cancel': 'Cancel',
  'enquiry.errName': 'Please fill in your name.',
  'enquiry.errEmail': 'That email address does not look right.',
  'enquiry.errCountry': 'Please choose a country — the park needs it for its tax return.',
  'enquiry.errPhone': 'A phone number can only contain digits and + - ( ) . and spaces.',
  'enquiry.errShape': 'That does not look right.',
  'enquiry.errTooLong': 'That is longer than we can store.',
  'enquiry.failed': 'That did not go through. Please try once more.',
  'enquiry.sentAgain': 'Search again',

  /* ----------------------------------------------- the enquiry's own page */
  /*
    A guest reads this after pressing send, and again if they follow the link
    in their mail. It is deliberately not a confirmation: nothing is held, and
    every sentence here has to keep saying so without sounding like a refusal.
  */
  'sent.heading': 'Thank you — your request has arrived.',
  'sent.intro': 'We have your request. You will get an answer at {email}.',
  /* The park's own promise, and only where it has made one. Hedged in every
     language: most enquiries are answered quickly, but a long stay or a
     seasonal contract can take a different route. */
  'sent.replyHours': 'We usually reply within {count} hours.',
  'sent.replyDays': 'We usually reply within {count} days.',
  'sent.reference': 'Your reference is',
  'sent.loading': 'Fetching your request…',
  'sent.gone': 'We cannot show this request any more. The park can still find it by the reference below.',
  'sent.stay': 'What you asked for',
  'sent.accommodation': 'Accommodation',
  'sent.unit': 'Requested',
  'sent.arrival': 'Arrival',
  'sent.departure': 'Departure',
  'sent.nights': 'Nights',
  'sent.party': 'Party',
  'sent.price': 'Price',
  'sent.indication': 'This amount is an indication. The price the park confirms by reply is the one that counts.',
  'sent.you': 'What you told us',
  'sent.name': 'Name',
  'sent.email': 'Email',
  'sent.phone': 'Phone',
  'sent.address': 'Address',
  'sent.vehicle': 'Registration',
  'sent.comment': 'Your message',
  'sent.wrong': 'Something not right? Reply to the email we just sent you and we will correct it.',


  /* ---------------------------------------------------------------- errors */
  'error.load': 'We could not load what the park has on offer.',
  'error.availability': 'We could not check those dates just now.',
  'error.retry': 'Try again',
} as const;

export type Catalogue = { [K in keyof typeof en]: string | Plural };
export type MessageKey = keyof typeof en;
